// useAutoTopUp.ts — Reads auto-top-up config + provides action dispatchers.
//
// Reads: get_auto_top_up_config() (query — fast, no cycles)
// Writes: set_auto_top_up_enabled, set_auto_top_up_threshold,
//         set_auto_top_up_amount, set_daily_top_up_cap, auto_top_up_check
//
// All writes are confirmation-gated in the UI — this hook only dispatches;
// confirmation modals live in the Controls page component.
//
// Security: every write call is admin-gated at the canister level.
// The frontend sends the II identity on every call; the canister rejects
// non-admin principals before touching state. (canister-security skill)

import { useCallback, useEffect, useRef, useState } from 'react';
import type { Identity } from '@dfinity/agent';
import {
  createAdminAgent,
  createAdminActor,
  type AutoTopUpConfig,
  type TopUpSummary,
} from '../lib/adminAgent';

const POLL_INTERVAL_MS = 60_000;

export interface UseAutoTopUpReturn {
  config: AutoTopUpConfig | null;
  isLoading: boolean;
  isMutating: boolean;
  error: string | null;
  mutationError: string | null;
  lastTopUpSummary: TopUpSummary | null;
  refresh: () => void;
  setEnabled: (enabled: boolean) => Promise<void>;
  setThreshold: (threshold: bigint) => Promise<void>;
  setAmount: (amount: bigint) => Promise<void>;
  setDailyCap: (cap: bigint) => Promise<void>;
  runTopUpCheck: () => Promise<TopUpSummary | null>;
}

export function useAutoTopUp(identity: Identity | null): UseAutoTopUpReturn {
  const [config, setConfig] = useState<AutoTopUpConfig | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isMutating, setIsMutating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [lastTopUpSummary, setLastTopUpSummary] = useState<TopUpSummary | null>(null);

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isMountedRef = useRef(true);

  const fetchConfig = useCallback(async () => {
    if (!identity) return;
    setIsLoading(true);
    setError(null);
    try {
      const agent = await createAdminAgent(identity);
      const actor = createAdminActor(agent);
      const cfg = await actor.get_auto_top_up_config();
      if (isMountedRef.current) setConfig(cfg);
    } catch (err) {
      if (isMountedRef.current) {
        const msg = err instanceof Error ? err.message : 'Failed to fetch auto-top-up config.';
        setError(msg);
      }
    } finally {
      if (isMountedRef.current) setIsLoading(false);
    }
  }, [identity]);

  const refresh = useCallback(() => { fetchConfig(); }, [fetchConfig]);

  useEffect(() => {
    if (!identity) return;
    isMountedRef.current = true;
    fetchConfig();
    timerRef.current = setInterval(fetchConfig, POLL_INTERVAL_MS);
    return () => {
      isMountedRef.current = false;
      if (timerRef.current !== null) clearInterval(timerRef.current);
    };
  }, [identity, fetchConfig]);

  // ---------------------------------------------------------------------------
  // Mutators — each dispatches a single setter, then re-fetches config.
  // ---------------------------------------------------------------------------

  const mutate = useCallback(async (action: () => Promise<void>) => {
    if (!identity) return;
    setIsMutating(true);
    setMutationError(null);
    try {
      await action();
      await fetchConfig();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Action failed.';
      if (isMountedRef.current) setMutationError(msg);
    } finally {
      if (isMountedRef.current) setIsMutating(false);
    }
  }, [identity, fetchConfig]);

  const setEnabled = useCallback(async (enabled: boolean) => {
    await mutate(async () => {
      const agent = await createAdminAgent(identity!);
      const actor = createAdminActor(agent);
      const result = await actor.set_auto_top_up_enabled(enabled);
      if ('err' in result) throw new Error(result.err);
    });
  }, [identity, mutate]);

  const setThreshold = useCallback(async (threshold: bigint) => {
    await mutate(async () => {
      const agent = await createAdminAgent(identity!);
      const actor = createAdminActor(agent);
      const result = await actor.set_auto_top_up_threshold(threshold);
      if ('err' in result) throw new Error(result.err);
    });
  }, [identity, mutate]);

  const setAmount = useCallback(async (amount: bigint) => {
    await mutate(async () => {
      const agent = await createAdminAgent(identity!);
      const actor = createAdminActor(agent);
      const result = await actor.set_auto_top_up_amount(amount);
      if ('err' in result) throw new Error(result.err);
    });
  }, [identity, mutate]);

  const setDailyCap = useCallback(async (cap: bigint) => {
    await mutate(async () => {
      const agent = await createAdminAgent(identity!);
      const actor = createAdminActor(agent);
      const result = await actor.set_daily_top_up_cap(cap);
      if ('err' in result) throw new Error(result.err);
    });
  }, [identity, mutate]);

  const runTopUpCheck = useCallback(async (): Promise<TopUpSummary | null> => {
    if (!identity) return null;
    setIsMutating(true);
    setMutationError(null);
    try {
      const agent = await createAdminAgent(identity);
      const actor = createAdminActor(agent);
      const result = await actor.auto_top_up_check();
      if ('err' in result) throw new Error(result.err);
      if (isMountedRef.current) setLastTopUpSummary(result.ok);
      await fetchConfig();
      return result.ok;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Top-up check failed.';
      if (isMountedRef.current) setMutationError(msg);
      return null;
    } finally {
      if (isMountedRef.current) setIsMutating(false);
    }
  }, [identity, fetchConfig]);

  return {
    config,
    isLoading,
    isMutating,
    error,
    mutationError,
    lastTopUpSummary,
    refresh,
    setEnabled,
    setThreshold,
    setAmount,
    setDailyCap,
    runTopUpCheck,
  };
}
